import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Btn } from "./UI";
import { X, Check, SkipForward, ExternalLink, PartyPopper, Coffee, Flame } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_STYLE = { low: "bg-[#FFF9C4] text-[#8D6E00]", medium: "bg-[#FFE0B2] text-[#8D5A00]", high: "bg-[#FFCDD2] text-[#C62828]", critical: "bg-[#C62828] text-white" };

export function TaskFocusMode({ open, onClose, doneKeys = ["completed", "archived"], onCompleted }) {
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [doneCount, setDoneCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setIdx(0); setDoneCount(0); setQueue(null);
    api.get("/tasks", { params: { view: "assigned" } }).then((r) => {
      const today = dayjs().format("YYYY-MM-DD");
      setQueue(r.data
        .filter((t) => !doneKeys.includes(t.status) && t.due_date && t.due_date <= today)
        .sort((a, b) => (a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0)));
    }).catch(() => setQueue([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;
  const total = queue?.length || 0;
  const t = queue?.[idx];
  const finished = queue !== null && idx >= total;
  const overdueDays = t?.due_date ? dayjs().startOf("day").diff(dayjs(t.due_date), "day") : 0;

  const complete = async () => {
    setBusy(true);
    try {
      await api.put(`/tasks/${t.id}`, { status: "completed" });
      setDoneCount((c) => c + 1);
      setIdx((i) => i + 1);
      onCompleted && onCompleted();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[#2A1B18]/95 p-4 backdrop-blur-sm" data-testid="task-focus-mode">
      <button data-testid="focus-exit-btn" aria-label="Exit focus mode" onClick={onClose}
        className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"><X size={18} /></button>

      {queue === null ? (
        <div className="text-sm text-white/70">Loading your day…</div>
      ) : total === 0 ? (
        <div className="text-center text-white" data-testid="focus-empty-state">
          <Coffee size={44} className="mx-auto mb-4 text-[#FFB300]" />
          <h2 className="font-heading text-2xl font-bold">Nothing due today</h2>
          <p className="mt-2 text-sm text-white/70">No assigned tasks are due or overdue. Enjoy the calm, or plan ahead on the board.</p>
          <Btn className="mt-6" data-testid="focus-close-empty" onClick={onClose}>Back to Tasks</Btn>
        </div>
      ) : finished ? (
        <div className="text-center text-white" data-testid="focus-done-state">
          <PartyPopper size={44} className="mx-auto mb-4 text-[#FFB300]" />
          <h2 className="font-heading text-2xl font-bold">Day complete!</h2>
          <p className="mt-2 text-sm text-white/70">You worked through {total} task{total === 1 ? "" : "s"} and completed {doneCount}.</p>
          <Btn className="mt-6" data-testid="focus-close-done" onClick={onClose}>Done</Btn>
        </div>
      ) : (
        <div className="w-full max-w-xl" data-testid="focus-card">
          <div className="mb-3 flex items-center justify-between text-xs font-bold uppercase tracking-wide text-white/60">
            <span data-testid="focus-progress-label">Task {idx + 1} of {total}</span>
            <span>{doneCount} completed</span>
          </div>
          <div className="mb-5 h-1.5 overflow-hidden rounded-full bg-white/15">
            <div className="h-full rounded-full bg-[#FFB300] transition-all duration-500" style={{ width: `${(idx / total) * 100}%` }} data-testid="focus-progress-bar" />
          </div>
          <div className="rounded-3xl bg-[#FDFCF8] p-7 shadow-2xl">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.medium}`}>{t.priority}</span>
              {overdueDays > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#FFEBEE] px-2.5 py-0.5 text-[10px] font-bold text-[#C62828]" data-testid="focus-overdue-chip">
                  <Flame size={10} /> {overdueDays} day{overdueDays === 1 ? "" : "s"} overdue
                </span>
              )}
              <span className="ml-auto text-xs tabular-nums text-[#7B6157]">Due {dayjs(t.due_date).format("MMM D")}</span>
            </div>
            <h2 className="font-heading text-xl font-bold text-[#3E2723]" data-testid="focus-task-title">{t.title}</h2>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#7B6157]">
              {t.client_name && <span>Client: <span className="font-semibold text-[#5D4037]">{t.client_name}</span></span>}
              {t.project && <span>Project: <span className="font-semibold text-[#5D4037]">{t.project}</span></span>}
              {t.module && <span>Module: <span className="font-semibold text-[#5D4037]">{t.module}</span></span>}
            </div>
            {t.summary && <p className="mt-3 rounded-xl bg-[#F5F2EB] px-4 py-3 text-sm leading-relaxed text-[#4E342E]">{t.summary.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}</p>}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Btn data-testid="focus-complete-btn" disabled={busy} onClick={complete}
                className="bg-[#2E7D32] hover:bg-[#1B5E20] px-5"><Check size={15} /> {busy ? "Saving…" : "Complete"}</Btn>
              <Btn variant="outline" data-testid="focus-skip-btn" onClick={() => setIdx((i) => i + 1)}><SkipForward size={14} /> Skip for now</Btn>
              <button data-testid="focus-open-task-btn" onClick={() => { onClose(); navigate(`/tasks/${t.id}`); }}
                className="ml-auto flex items-center gap-1 text-xs font-semibold text-[#7B6157] hover:text-[#4E342E] hover:underline">
                <ExternalLink size={12} /> Open full task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
